/**
 * Resolves the cover-photo URL to display for a project card.
 *
 * Data model (set by the iOS app — see
 * `Housfully/Housfully/PhotoGalleryViewController.swift` and
 * `Housfully/Housfully/Models/Project.swift`):
 *
 *   - `project.imageUrl` — explicit cover photo URL. Set by iOS
 *     `ProjectService.setProjectCoverPhoto` (writes `imageUrl` on the project
 *     doc). This is also what `ProjectDetail.js` renders as the hero image.
 *   - `project.galleryPhotos` — array of `{ url, createdAt }` photo objects.
 *     iOS sorts most-recent-first when rendering.
 *
 * Selection logic:
 *   1. If `imageUrl` is set, use it (explicit user choice).
 *   2. Otherwise, fall back to the most recent gallery photo.
 *   3. Otherwise, return `null` (caller renders its placeholder icon).
 *
 * Both iOS and the web app expose a "Set as cover" UI (web:
 * `components/ProjectPhotoGallery.js` → `projectService.setProjectCoverPhoto`;
 * iOS: `PhotoGalleryViewController` → `ProjectService.setProjectCoverPhoto`).
 *

 * @param {object|null|undefined} project Firestore project doc (already
 *   spread into `{ id, ...data }`). Safe to pass `null` / `undefined`.
 * @returns {string|null} A photo URL, or `null` if none available.
 */
export function getProjectCoverPhotoUrl(project) {
  if (!project) return null;

  if (typeof project.imageUrl === 'string' && project.imageUrl.length > 0) {
    return project.imageUrl;
  }

  const gallery = Array.isArray(project.galleryPhotos)
    ? project.galleryPhotos
    : null;
  if (!gallery || gallery.length === 0) return null;

  // Sort most-recent first (matches iOS gallery ordering). Tolerate the
  // various Firestore timestamp shapes (Timestamp, {seconds}, {_seconds},
  // ISO string, missing).
  const sorted = [...gallery].sort(
    (a, b) => _toMillis(b?.createdAt) - _toMillis(a?.createdAt)
  );

  for (const photo of sorted) {
    if (photo && typeof photo.url === 'string' && photo.url.length > 0) {
      return photo.url;
    }
  }
  return null;
}

function _toMillis(timestamp) {
  if (!timestamp) return 0;
  if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
  if (typeof timestamp.toDate === 'function')
    return timestamp.toDate().getTime();
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  if (typeof timestamp._seconds === 'number') return timestamp._seconds * 1000;
  if (typeof timestamp === 'string' || timestamp instanceof Date) {
    const t = new Date(timestamp).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}
